import React, { useState } from 'react';
import { Box, Typography, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { styled } from 'styled-components';
import Input from './Input';

const PasswordContainer = styled(Box)`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 1rem;
	margin: 2rem 0;
`;

const InstructionsBox = styled(Box)`
	border-radius: 8px;
	padding: 1.5rem;
	text-align: center;
	max-width: 500px;
`;

interface PasswordInputProps {
	value: string | undefined;
	onChange: (value: string) => void;
	error?: string;
	disabled?: boolean;
	placeholder?: string;
	showInstructions?: boolean;
}

export default function PasswordInput({
	value,
	onChange,
	error,
	disabled = false,
	placeholder = 'Enter password...',
	showInstructions = true,
}: PasswordInputProps) {
	const [showPassword, setShowPassword] = useState(false);

	// Ensure value is never undefined for safe operations
	const safeValue = value || '';

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = event.target.value.toUpperCase().slice(0, 6); // Limit to 6 characters and uppercase
		onChange(newValue);
	};

	const handleTogglePasswordVisibility = () => {
		setShowPassword(!showPassword);
	};

	const isValidFormat = (password: string): boolean => {
		return /^[A-Z0-9]{6}$/.test(password);
	};

	const getHelperText = (): string => {
		if (error) return error;
		if (safeValue.length === 0)
			return 'Enter the 6-character password from your email';
		if (safeValue.length < 6)
			return `${6 - safeValue.length} more character${
				6 - safeValue.length !== 1 ? 's' : ''
			} needed`;
		if (!isValidFormat(safeValue))
			return 'Password must contain only letters and numbers';
		return 'Password format is correct';
	};

	return (
		<PasswordContainer>
			{showInstructions && (
				<InstructionsBox>
					<Typography variant="h6" gutterBottom color="primary">
						Password Required
					</Typography>
					<Typography
						variant="body2"
						color="text.secondary"
						gutterBottom
					>
						Check your email for a{' '}
						<strong>6-character password</strong>
					</Typography>
					<Typography variant="body2" color="text.secondary">
						The password contains letters and numbers and is
						case-sensitive
					</Typography>
				</InstructionsBox>
			)}

			<Input
				value={safeValue}
				onChange={handleChange}
				type={showPassword ? 'text' : 'password'}
				placeholder={placeholder}
				disabled={disabled}
				error={
					!!error ||
					(safeValue.length === 6 && !isValidFormat(safeValue))
				}
				helperText={getHelperText()}
				fullWidth
				inputProps={{
					maxLength: 6,
					style: { textAlign: 'center' },
				}}
				InputProps={{
					endAdornment: (
						<InputAdornment position="end">
							<IconButton
								aria-label="toggle password visibility"
								onClick={handleTogglePasswordVisibility}
								edge="end"
							>
								{showPassword ? (
									<VisibilityOff />
								) : (
									<Visibility />
								)}
							</IconButton>
						</InputAdornment>
					),
				}}
				style={{
					maxWidth: 300,
				}}
			/>
		</PasswordContainer>
	);
}
